import {
  Check, Loader2, Pencil, Plus, Save, Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { InvoiceDrawerButton } from "@/features/activity-operations/components/InvoiceDrawerButton";
import { ACCOUNTING_DATE_BOUNDS } from "@/shared/configs/accountingDateBounds";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/Button";
import { Checkbox } from "@/shared/ui/Checkbox";
import { Input } from "@/shared/ui/Input";
import { Popover, PopoverAnchor, PopoverContent } from "@/shared/ui/Popover";
import { Skeleton } from "@/shared/ui/Skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/shared/ui/Tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import { NATURE_STYLES } from "../../configs/natureStyles";
import { SepaAllocationReturnNotice } from "../SepaAllocationReturnNotice";
import {
  CELL_EDITABLE_INPUT_CLASS,
  CREDIT_LOCKED_HINT,
  DEBIT_LOCKED_HINT,
  LOCKED_AMOUNT_INPUT_CLASS,
  formatAmount,
  formatCurrencyField,
  getAllocationCategory,
  getAmountSideLock,
  getAllocationCategoryEmoji,
  getAllocationCategoryLabel,
  getPreferredOperationTypeNature,
  groupOperationTypesByNature,
  normalizeSearchText,
  normalizeCurrencyTyping,
  resolveBudgetType,
} from "./utils";
import type { EditableAllocationState, OperationTypeNature, OperationTypeNatureFilter } from "./utils";
import type { LedgerAllocation, LedgerAllocationOperationType } from "../../types/accounting.types";

type SimpleAllocationsListProps = {
  allocations: LedgerAllocation[]
  isLoading: boolean
  getState: (allocation: LedgerAllocation) => EditableAllocationState
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  isAllocationDirty: (allocation: LedgerAllocation) => boolean
  onSave: (allocation: LedgerAllocation) => void
  onDelete: (id: string) => void
  isBusy: boolean
  operationPopoverAllocationId: string | null
  setOperationPopoverAllocationId: (id: string | null) => void
  operationPopoverSearchByAllocationId: Record<string, string>
  setOperationPopoverSearchByAllocationId: React.Dispatch<React.SetStateAction<Record<string, string>>>
  operationSearchBusyByAllocationId: Record<string, boolean>
  markOperationSearchBusy: (allocationId: string) => void
  operationTypesByBudget: Record<"ASC" | "AEP", LedgerAllocationOperationType[]>
  onFindOrCreateOperationType: (allocation: LedgerAllocation, forcedQuery?: string, forcedNature?: OperationTypeNature) => void
  isCreateOperationTypePending: boolean
  onOpenSimpleEditor: (allocationId: string) => void
  onOpenReimbursementEditor: (allocationId: string) => void
  onOpenEcommerceEditor: (allocationId: string) => void
  onOpenLegacyEditor: (allocationId: string) => void
  isCreatePending: boolean
  deletingAllocationId: string | null
  totalDebit: number
  totalCredit: number
  // Selection mode
  isSelectionMode?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  highlightedAllocationId?: string | null
};

const SimpleAllocationsList = ({
  allocations,
  isLoading,
  getState,
  setField,
  isAllocationDirty,
  onSave,
  onDelete,
  isBusy,
  operationPopoverAllocationId,
  setOperationPopoverAllocationId,
  operationPopoverSearchByAllocationId,
  setOperationPopoverSearchByAllocationId,
  operationSearchBusyByAllocationId,
  markOperationSearchBusy,
  operationTypesByBudget,
  onFindOrCreateOperationType,
  isCreateOperationTypePending,
  onOpenSimpleEditor,
  onOpenReimbursementEditor,
  onOpenEcommerceEditor,
  onOpenLegacyEditor,
  isCreatePending,
  deletingAllocationId,
  totalDebit,
  totalCredit,
  isSelectionMode = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  highlightedAllocationId,
}: SimpleAllocationsListProps) => {
  const allLocked = allocations.length > 0 && allocations.every((a) => a.locked);
  const hasAllocations = allocations.length > 0;
  const selectableAllocations = allocations.filter((a) => !a.locked && !a.settlementId);
  const allSelectableSelected = isSelectionMode && selectableAllocations.length > 0 && selectableAllocations.every((a) => selectedIds?.has(a.id));
  const someSelected = isSelectionMode && selectableAllocations.some((a) => selectedIds?.has(a.id));

  return (
    <div>
      <div className="mb-1.5 text-[11px] font-medium text-slate-600">Grand livre</div>
      <div className="rounded-md border border-slate-200 bg-white overflow-x-hidden overflow-y-auto select-text">
        <table className="w-full table-fixed leading-tight">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {isSelectionMode && (
                <th className="px-1 py-1 w-[28px] text-center">
                  <Checkbox
                    checked={allSelectableSelected}
                    indeterminate={someSelected && !allSelectableSelected}
                    onCheckedChange={() => onToggleSelectAll?.()}
                    className="size-3.5"
                  />
                </th>
              )}
              <th className="text-left px-1 py-1 w-[28px] text-[9px] uppercase tracking-wide text-slate-600"> </th>
              <th className="text-left px-2 py-1 w-[90px] text-[9px] uppercase tracking-wide text-slate-600">Date</th>
              <th className="text-left px-2 py-1 w-[64px] text-[9px] uppercase tracking-wide text-slate-600">Budget</th>
              <th className="text-left px-2 py-1 w-[110px] text-[9px] uppercase tracking-wide text-slate-600">Opération</th>
              <th className="text-left px-2 py-1 text-[9px] uppercase tracking-wide text-slate-600">Libellé</th>
              <th className="text-right px-2 py-1 w-[88px] text-[9px] uppercase tracking-wide text-slate-600">Débit</th>
              <th className="text-right px-2 py-1 w-[88px] text-[9px] uppercase tracking-wide text-slate-600">Crédit</th>
              {!allLocked && (
                <th className="text-right px-2 py-1 w-[96px] text-[9px] uppercase tracking-wide text-slate-600">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="text-[12px]">
            {isLoading && (
              Array.from({ length: 6 }).map((_, index) => (
                <tr key={`allocation-skeleton-${index}`} className="border-b border-slate-100 last:border-b-0 align-top">
                  {isSelectionMode && <td className="px-1 py-1.5"><Skeleton className="h-3.5 w-3.5 rounded-sm" /></td>}
                  <td className="px-1 py-1.5"><Skeleton className="h-5 w-5 rounded-sm" /></td>
                  <td className="px-2 py-1.5"><Skeleton className="h-6 w-16 rounded-sm" /></td>
                  <td className="px-2 py-1.5"><Skeleton className="h-6 w-16 rounded-sm" /></td>
                  <td className="px-2 py-1.5"><Skeleton className="h-6 w-20 rounded-sm" /></td>
                  <td className="px-2 py-1.5"><Skeleton className="h-6 w-full rounded-sm" /></td>
                  <td className="px-2 py-1.5"><Skeleton className="h-6 w-20 ml-auto rounded-sm" /></td>
                  <td className="px-2 py-1.5"><Skeleton className="h-6 w-20 ml-auto rounded-sm" /></td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-end gap-1">
                      <Skeleton className="h-6 w-6 rounded-sm" />
                      <Skeleton className="h-6 w-6 rounded-sm" />
                    </div>
                  </td>
                </tr>
              ))
            )}

            {!isLoading && allocations.length === 0 && (
              <tr>
                <td className="px-1.5 py-1.5 text-gray-500" colSpan={isSelectionMode ? 9 : 8}>Aucune affectation.</td>
              </tr>
            )}

            {!isLoading && allocations.map((allocation) => (
              <SimpleAllocationRow
                key={allocation.id}
                allocation={allocation}
                row={getState(allocation)}
                isDirty={isAllocationDirty(allocation)}
                onSave={() => onSave(allocation)}
                onDelete={() => onDelete(allocation.id)}
                isBusy={isBusy}
                setField={setField}
                operationPopoverAllocationId={operationPopoverAllocationId}
                setOperationPopoverAllocationId={setOperationPopoverAllocationId}
                operationPopoverSearch={operationPopoverSearchByAllocationId[allocation.id] ?? ""}
                setOperationPopoverSearch={(value) => {
                  setOperationPopoverSearchByAllocationId((previous) => ({
                    ...previous,
                    [allocation.id]: value,
                  }));
                }}
                isOperationSearchBusy={Boolean(operationSearchBusyByAllocationId[allocation.id])}
                markOperationSearchBusy={() => markOperationSearchBusy(allocation.id)}
                operationTypesByBudget={operationTypesByBudget}
                onFindOrCreateOperationType={onFindOrCreateOperationType}
                isCreateOperationTypePending={isCreateOperationTypePending}
                onOpenSimpleEditor={() => onOpenSimpleEditor(allocation.id)}
                onOpenReimbursementEditor={() => onOpenReimbursementEditor(allocation.id)}
                onOpenEcommerceEditor={() => onOpenEcommerceEditor(allocation.id)}
                onOpenLegacyEditor={() => onOpenLegacyEditor(allocation.id)}
                isDeleting={deletingAllocationId === allocation.id}
                isSelectionMode={isSelectionMode}
                isSelected={selectedIds?.has(allocation.id) ?? false}
                onToggleSelect={() => onToggleSelect?.(allocation.id)}
                isHighlighted={highlightedAllocationId === allocation.id}
              />
            ))}

            {isCreatePending && (
              <tr className="border-b border-slate-100 last:border-b-0">
                <td colSpan={(allLocked ? 7 : 8) + (isSelectionMode ? 1 : 0)} className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-2 text-[11px] text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Ajout de l&apos;affectation...
                  </span>
                </td>
              </tr>
            )}

            {!isLoading && hasAllocations && (
              <tr className="bg-slate-50 border-t border-slate-200">
                <td className="px-2 py-1.5 font-semibold text-slate-700" colSpan={5 + (isSelectionMode ? 1 : 0)}>
                  Total
                </td>
                <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-slate-700">
                  {formatAmount(totalDebit)}
                </td>
                <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-slate-700">
                  {formatAmount(totalCredit)}
                </td>
                {!allLocked && <td className="px-2 py-1.5" />}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const NATURE_LABELS = {
  expense: "Dépenses",
  revenue: "Recettes",
} as const;

// Mêmes teintes que NATURE_STYLES, préfixées pour l'état actif (le trigger force un fond blanc).
const NATURE_FILTERS: Array<{ value: OperationTypeNatureFilter, label: string, className?: string }> = [
  { value: "all", label: "Tout" },
  {
    value: "expense",
    label: NATURE_LABELS.expense,
    className: "text-amber-800 dark:text-amber-300 data-[state=active]:!bg-amber-50 data-[state=active]:border-amber-300 dark:data-[state=active]:!bg-amber-950 dark:data-[state=active]:border-amber-700",
  },
  {
    value: "revenue",
    label: NATURE_LABELS.revenue,
    className: "text-emerald-800 dark:text-emerald-300 data-[state=active]:!bg-emerald-50 data-[state=active]:border-emerald-300 dark:data-[state=active]:!bg-emerald-950 dark:data-[state=active]:border-emerald-700",
  },
];

type SimpleAllocationRowProps = {
  allocation: LedgerAllocation
  row: EditableAllocationState
  isDirty: boolean
  onSave: () => void
  onDelete: () => void
  isBusy: boolean
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  operationPopoverAllocationId: string | null
  setOperationPopoverAllocationId: (id: string | null) => void
  operationPopoverSearch: string
  setOperationPopoverSearch: (value: string) => void
  isOperationSearchBusy: boolean
  markOperationSearchBusy: () => void
  operationTypesByBudget: Record<"ASC" | "AEP", LedgerAllocationOperationType[]>
  onFindOrCreateOperationType: (allocation: LedgerAllocation, forcedQuery?: string, forcedNature?: OperationTypeNature) => void
  isCreateOperationTypePending: boolean
  onOpenSimpleEditor: () => void
  onOpenReimbursementEditor: () => void
  onOpenEcommerceEditor: () => void
  onOpenLegacyEditor: () => void
  isDeleting: boolean
  // Selection mode
  isSelectionMode?: boolean
  isSelected?: boolean
  onToggleSelect?: () => void
  isHighlighted?: boolean
};

const SimpleAllocationRow = ({
  allocation,
  row,
  isDirty,
  onSave,
  onDelete,
  isBusy,
  setField,
  operationPopoverAllocationId,
  setOperationPopoverAllocationId,
  operationPopoverSearch,
  setOperationPopoverSearch,
  isOperationSearchBusy,
  markOperationSearchBusy,
  operationTypesByBudget,
  onFindOrCreateOperationType,
  isCreateOperationTypePending,
  onOpenSimpleEditor,
  onOpenReimbursementEditor,
  onOpenEcommerceEditor,
  onOpenLegacyEditor,
  isDeleting,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
  isHighlighted = false,
}: SimpleAllocationRowProps) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [natureFilter, setNatureFilter] = useState<OperationTypeNatureFilter>("all");
  const allocationCategory = getAllocationCategory(allocation);
  const emoji = getAllocationCategoryEmoji(allocationCategory);
  const categoryLabel = getAllocationCategoryLabel(allocationCategory);

  const budgetType = resolveBudgetType(row.budgetType || allocation.budgetType);
  const { isDebitLocked, isCreditLocked } = getAmountSideLock(row.debit, row.credit);
  const options = operationTypesByBudget[budgetType];
  const normalizedPopoverSearch = normalizeSearchText(operationPopoverSearch);
  const filteredOptions = useMemo(() => {
    if (!normalizedPopoverSearch) return options;
    return options.filter((operationType) => normalizeSearchText(operationType.name).includes(normalizedPopoverSearch));
  }, [options, normalizedPopoverSearch]);
  const preferredNature = getPreferredOperationTypeNature(row.debit, row.credit);
  const natureCounts = useMemo(() => {
    const revenueCount = filteredOptions.filter((operationType) => operationType.nature === "revenue").length;
    return { all: filteredOptions.length, expense: filteredOptions.length - revenueCount, revenue: revenueCount };
  }, [filteredOptions]);
  const optionGroups = useMemo(
    () => groupOperationTypesByNature(filteredOptions, preferredNature, natureFilter),
    [filteredOptions, preferredNature, natureFilter],
  );
  // Nature donnée à une catégorie créée d'ici : l'onglet choisi, sinon le sens de l'écriture.
  const createNature = natureFilter === "all" ? preferredNature : natureFilter;
  const popoverExactMatch = options.find((operationType) => normalizeSearchText(operationType.name) === normalizedPopoverSearch
    && (!createNature || (operationType.nature ?? "expense") === createNature)) ?? null;
  const showCreateButton = Boolean(operationPopoverSearch.trim()) && !popoverExactMatch;
  const isPopoverOpen = operationPopoverAllocationId === allocation.id;

  const LOCKED_CELL_CLASS = "h-7 px-2 flex items-center text-[12px] text-slate-500";

  if (allocation.locked) {
    return (
      <tr className="border-b border-slate-100 last:border-b-0 align-top bg-slate-50/50">
        {isSelectionMode && <td className="px-1 py-1.5 text-center align-middle" />}
        <td className="px-1 py-1.5 text-center align-middle">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-6 w-6 items-center justify-center text-[14px] leading-none cursor-default">{emoji}</span>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">{categoryLabel}</TooltipContent>
          </Tooltip>
        </td>
        <td className="py-1.5"><div className={LOCKED_CELL_CLASS}>{row.date || "-"}</div></td>
        <td className="py-1.5"><div className={LOCKED_CELL_CLASS}>{row.budgetType || "-"}</div></td>
        <td className="py-1.5 w-[110px] max-w-[110px]">
          <div className={`${LOCKED_CELL_CLASS} truncate`}>{row.operationType || "-"}</div>
        </td>
        <td className="py-1.5 min-w-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <div className={`${LOCKED_CELL_CLASS} truncate`}>{row.description || "-"}</div>
                <SepaAllocationReturnNotice allocation={allocation} />
                {allocation.refundStatusName && (
                  <div className="px-2 pb-0.5 text-[10px] text-slate-400 italic truncate">
                    {allocation.sepaReturns?.length ? "Paiement initial · fonds retournés" : allocation.refundStatusName}
                  </div>
                )}
                {allocation.ecommerceOrderStatusName && (
                  <div className="px-2 pb-0.5 text-[10px] text-slate-400 italic truncate">
                    {allocation.ecommerceOrderStatusName}
                  </div>
                )}
                {allocation.settlementId && (
                  <div className="px-2 pb-0.5 text-[10px] text-indigo-400 italic truncate">
                    Rgt
                    {" "}
                    {allocation.settlementNumber}
                    {allocation.settlementIssuer ? ` — ${allocation.settlementIssuer}` : ""}
                    {allocation.settlementPaymentMethod ? ` (${allocation.settlementPaymentMethod})` : ""}
                  </div>
                )}
                {allocation.otherDescription && (
                  <div className="px-2 pb-0.5 text-[10px] text-slate-400 italic truncate" title={allocation.otherDescription}>
                    {allocation.otherDescription}
                  </div>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[680px] break-words">
              {row.description || "-"}
              {allocation.otherDescription && (
                <>
                  <br />
                  <span className="text-slate-400 italic">{allocation.otherDescription}</span>
                </>
              )}
            </TooltipContent>
          </Tooltip>
        </td>
        <td className="py-1.5">
          <div className={`${LOCKED_CELL_CLASS} justify-end tabular-nums`}>{formatAmount(allocation.debit ?? 0)}</div>
        </td>
        <td className="py-1.5">
          <div className={`${LOCKED_CELL_CLASS} justify-end tabular-nums`}>{formatAmount(allocation.credit ?? 0)}</div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-b border-slate-100 last:border-b-0 align-top${isDeleting ? " opacity-50" : ""}${isHighlighted ? " bg-amber-50 ring-1 ring-inset ring-amber-300" : isSelected ? " bg-indigo-50/50" : ""}`}>
      {isSelectionMode && (
        <td className="px-1 py-1.5 text-center align-middle">
          {allocation.settlementId
            ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Checkbox checked={false} disabled className="size-3.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs max-w-[200px]">
                  Liée à un règlement — déplacez le règlement pour déplacer ses écritures
                </TooltipContent>
              </Tooltip>
            )
            : (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelect?.()}
                className="size-3.5"
              />
            )}
        </td>
      )}
      <td className="px-1 py-1.5 text-center align-middle">
        {isDeleting
          ? <Loader2 className="inline h-4 w-4 animate-spin text-slate-400" />
          : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex h-6 w-6 items-center justify-center text-[14px] leading-none cursor-default">{emoji}</span>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">{categoryLabel}</TooltipContent>
            </Tooltip>
          )}
      </td>
      <td className="p-0">
        <Input
          type="date"
          min={ACCOUNTING_DATE_BOUNDS.min}
          max={ACCOUNTING_DATE_BOUNDS.max}
          value={row.date}
          onChange={(event) => setField(allocation.id, "date", event.target.value, allocation)}
          className={CELL_EDITABLE_INPUT_CLASS}
        />
      </td>
      <td className="p-0">
        <select
          value={row.budgetType || ""}
          onChange={(event) => {
            setField(allocation.id, "budgetType", event.target.value, allocation);
            setField(allocation.id, "operationTypeId", "", allocation);
          }}
          className={[CELL_EDITABLE_INPUT_CLASS, "appearance-none"].join(" ")}
        >
          <option value="ASC">ASC</option>
          <option value="AEP">AEP</option>
          {row.budgetType && !["ASC", "AEP"].includes(row.budgetType) && (
            <option value={row.budgetType}>{row.budgetType}</option>
          )}
        </select>
      </td>
      <td className="p-0 w-[110px] max-w-[110px]">
        <Popover
          open={isPopoverOpen}
          onOpenChange={(nextOpen) => {
            setOperationPopoverAllocationId(nextOpen ? allocation.id : null);
            if (!nextOpen) {
              setOperationPopoverSearch("");
              setNatureFilter("all");
            }
          }}
        >
          <PopoverAnchor asChild>
            <div className="relative">
              <Input
                value={row.operationType}
                title={row.operationType || allocation.operationTypeName || "-"}
                onFocus={() => setOperationPopoverAllocationId(allocation.id)}
                onClick={() => setOperationPopoverAllocationId(allocation.id)}
                onChange={(event) => {
                  setField(allocation.id, "operationType", event.target.value, allocation);
                  setField(allocation.id, "operationTypeId", "", allocation);
                  markOperationSearchBusy();
                  setOperationPopoverAllocationId(allocation.id);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    onFindOrCreateOperationType(allocation);
                  }
                }}
                className={`${CELL_EDITABLE_INPUT_CLASS} pr-8`}
              />
              <div className="absolute inset-y-0 right-1 flex items-center">
                {(isOperationSearchBusy || isCreateOperationTypePending) && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                )}
              </div>
            </div>
          </PopoverAnchor>
          <PopoverContent
            align="start"
            className="w-[280px] p-1"
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
          >
            <div className="p-1">
              <Input
                value={operationPopoverSearch}
                onChange={(event) => setOperationPopoverSearch(event.target.value)}
                placeholder="Rechercher une catégorie"
                className="h-7 text-[11px]"
              />
              <Tabs
                value={natureFilter}
                onValueChange={(value) => setNatureFilter(value as OperationTypeNatureFilter)}
                className="mt-1"
              >
                <TabsList className="w-full grid grid-cols-3 h-7">
                  {NATURE_FILTERS.map((filter) => (
                    <TabsTrigger key={filter.value} value={filter.value} className={cn("text-[10px] px-1", filter.className)}>
                      {filter.label}
                      <span className="ml-1 inline-flex items-center justify-center rounded-full bg-slate-200 px-1 py-0.5 text-[9px] font-medium leading-none text-slate-700">
                        {natureCounts[filter.value]}
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <div
              className="max-h-44 overflow-y-auto overscroll-contain"
              onWheel={(event) => event.stopPropagation()}
              onTouchMove={(event) => event.stopPropagation()}
            >
              {optionGroups.length === 0 && (
                <div className="px-2 py-1.5 text-[11px] text-slate-500">Aucune catégorie trouvée</div>
              )}
              {optionGroups.map((group) => (
                <div key={group.nature} role="group" aria-label={NATURE_LABELS[group.nature]}>
                  <div className={cn("sticky top-0 z-10 bg-popover px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide", NATURE_STYLES[group.nature].text)}>
                    {NATURE_LABELS[group.nature]}
                    {group.nature === preferredNature && (
                      <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground">
                        {`- suggérées pour un ${preferredNature === "expense" ? "débit" : "crédit"}`}
                      </span>
                    )}
                  </div>
                  {group.operationTypes.map((operationType) => (
                    <button
                      key={operationType.id}
                      type="button"
                      className="w-full flex items-center gap-2 text-left px-2 py-1.5 text-[11px] rounded hover:bg-slate-100"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setField(allocation.id, "operationTypeId", operationType.id, allocation);
                        setField(allocation.id, "operationType", operationType.name, allocation);
                        setOperationPopoverAllocationId(null);
                        setNatureFilter("all");
                      }}
                    >
                      <span
                        aria-hidden="true"
                        className="size-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: NATURE_STYLES[group.nature].chartColor }}
                      />
                      {operationType.name}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            {showCreateButton && (
              <div className="border-t mt-1 pt-1">
                <button
                  type="button"
                  className="w-full flex items-center gap-1 px-2 py-1.5 text-[11px] rounded hover:bg-slate-100 text-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onFindOrCreateOperationType(allocation, operationPopoverSearch.trim(), createNature ?? undefined)}
                  disabled={isCreateOperationTypePending}
                >
                  {isCreateOperationTypePending
                    ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Création...
                      </>
                    )
                    : (
                      <>
                        <Plus className="h-3.5 w-3.5" />
                        Créer &quot;
                        {operationPopoverSearch.trim()}
                        &quot;
                        {createNature && ` en ${createNature === "expense" ? "dépense" : "recette"}`}
                      </>
                    )}
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </td>
      <td className="p-0 min-w-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Input
                value={row.description}
                onChange={(event) => setField(allocation.id, "description", event.target.value, allocation)}
                className={CELL_EDITABLE_INPUT_CLASS}
              />
              <SepaAllocationReturnNotice allocation={allocation} />
              {allocation.refundStatusName && (
                <div className="px-2 pb-0.5 text-[10px] text-slate-400 italic truncate">
                  {allocation.sepaReturns?.length ? "Paiement initial · fonds retournés" : allocation.refundStatusName}
                </div>
              )}
              {allocation.ecommerceOrderStatusName && (
                <div className="px-2 pb-0.5 text-[10px] text-slate-400 italic truncate">
                  {allocation.ecommerceOrderStatusName}
                </div>
              )}
              {allocation.settlementId && (
                <div className="px-2 pb-0.5 text-[10px] text-indigo-400 italic truncate">
                  Rgt
                  {" "}
                  {allocation.settlementNumber}
                  {allocation.settlementIssuer ? ` — ${allocation.settlementIssuer}` : ""}
                  {allocation.settlementPaymentMethod ? ` (${allocation.settlementPaymentMethod})` : ""}
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[680px] break-words">
            {row.description || "-"}
          </TooltipContent>
        </Tooltip>
      </td>
      <td className="p-0">
        <Input
          inputMode="decimal"
          value={row.debit}
          onChange={(event) => setField(allocation.id, "debit", normalizeCurrencyTyping(event.target.value), allocation)}
          onFocus={(event) => setField(allocation.id, "debit", normalizeCurrencyTyping(event.target.value), allocation)}
          onBlur={(event) => setField(allocation.id, "debit", formatCurrencyField(event.target.value), allocation)}
          className={`${CELL_EDITABLE_INPUT_CLASS} text-right tabular-nums ${isDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
          placeholder="0,00"
          data-testid="debit-amount"
          readOnly={isDebitLocked}
          aria-disabled={isDebitLocked}
          title={isDebitLocked ? DEBIT_LOCKED_HINT : undefined}
        />
      </td>
      <td className="p-0">
        <Input
          inputMode="decimal"
          value={row.credit}
          onChange={(event) => setField(allocation.id, "credit", normalizeCurrencyTyping(event.target.value), allocation)}
          onFocus={(event) => setField(allocation.id, "credit", normalizeCurrencyTyping(event.target.value), allocation)}
          onBlur={(event) => setField(allocation.id, "credit", formatCurrencyField(event.target.value), allocation)}
          className={`${CELL_EDITABLE_INPUT_CLASS} text-right tabular-nums ${isCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
          placeholder="0,00"
          data-testid="credit-amount"
          readOnly={isCreditLocked}
          aria-disabled={isCreditLocked}
          title={isCreditLocked ? CREDIT_LOCKED_HINT : undefined}
        />
      </td>
      <td className="px-2 py-1.5">
        <div className="flex justify-end gap-1">
          {allocation.invoiceId && (
            <InvoiceDrawerButton invoiceId={allocation.invoiceId} kind={allocation.invoiceKind} />
          )}
          {allocationCategory === "simple_entry" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onOpenSimpleEditor}
              disabled={isBusy}
              aria-label="Éditer l'écriture simple"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {allocationCategory === "reimbursement" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onOpenReimbursementEditor}
              disabled={isBusy}
              aria-label="Détails du remboursement"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {allocationCategory === "ecommerce" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onOpenEcommerceEditor}
              disabled={isBusy}
              aria-label="Détails de la commande Bon Plans"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {(allocationCategory === "legacy_purchase" || allocationCategory === "legacy_travel" || allocationCategory === "legacy_reimbursement" || allocationCategory === "legacy_rental") && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onOpenLegacyEditor}
              disabled={isBusy}
              aria-label="Détails de l'écriture legacy"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            type="button"
            variant={isDirty ? "success" : "outline"}
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onSave}
            disabled={isBusy || !isDirty}
            aria-label="Enregistrer l'écriture"
          >
            <Save className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant={isConfirmingDelete ? "destructive" : "destructiveGhost"}
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => {
              if (isConfirmingDelete) {
                onDelete();
                setIsConfirmingDelete(false);
              }
              else {
                setIsConfirmingDelete(true);
              }
            }}
            onBlur={() => setIsConfirmingDelete(false)}
            disabled={isBusy}
            aria-label={isConfirmingDelete ? "Confirmer la suppression" : "Supprimer l'écriture"}
          >
            {isConfirmingDelete ? <Check className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default SimpleAllocationsList;
